import { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Building2, Home, Hammer, Plus, Edit2, Trash2, DollarSign,
  Calendar, Percent, RefreshCw, CreditCard, X, Check,
  TrendingDown, Clock, AlertTriangle, ChevronRight
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOAN_TYPES = [
  { value: 'business', label: 'Business Loan', icon: Building2 },
  { value: 'property', label: 'Property Loan', icon: Home },
  { value: 'construction', label: 'Construction Loan', icon: Hammer }
];

const FREQUENCIES = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'bi-weekly', label: 'Bi-weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annual', label: 'Annual' }
];

function RecurringPayments() {
  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [upcoming, setUpcoming] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [formData, setFormData] = useState({
    payment_type: 'loan',
    name: '',
    amount: '',
    due_day: '1',
    frequency: 'monthly',
    loan_type: 'business',
    interest_rate: '',
    remaining_balance: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [paymentsRes, summaryRes, upcomingRes] = await Promise.all([
        axios.get(`${API}/payments`, { withCredentials: true }),
        axios.get(`${API}/payments/summary`, { withCredentials: true }),
        axios.get(`${API}/payments/upcoming?days=30`, { withCredentials: true })
      ]);
      setPayments(paymentsRes.data);
      setSummary(summaryRes.data);
      setUpcoming(upcomingRes.data);
    } catch (error) {
      console.error('Failed to fetch payments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        amount: parseFloat(formData.amount),
        due_day: parseInt(formData.due_day),
        interest_rate: formData.interest_rate ? parseFloat(formData.interest_rate) : null,
        remaining_balance: formData.remaining_balance ? parseFloat(formData.remaining_balance) : null
      };

      if (editingPayment) {
        await axios.put(`${API}/payments/${editingPayment.payment_id}`, data, { withCredentials: true });
      } else {
        await axios.post(`${API}/payments`, data, { withCredentials: true });
      }

      setShowForm(false);
      setEditingPayment(null);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Failed to save payment:', error);
    }
  };

  const handleEdit = (payment) => {
    setFormData({
      payment_type: payment.payment_type,
      name: payment.name,
      amount: payment.amount.toString(),
      due_day: payment.due_day.toString(),
      frequency: payment.frequency,
      loan_type: payment.loan_type || 'business',
      interest_rate: payment.interest_rate?.toString() || '',
      remaining_balance: payment.remaining_balance?.toString() || '',
      notes: payment.notes || ''
    });
    setEditingPayment(payment);
    setShowForm(true);
  };

  const handleDelete = async (paymentId) => {
    if (!window.confirm('Delete this recurring payment?')) return;
    try {
      await axios.delete(`${API}/payments/${paymentId}`, { withCredentials: true });
      fetchData();
    } catch (error) {
      console.error('Failed to delete payment:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      payment_type: 'loan',
      name: '',
      amount: '',
      due_day: '1',
      frequency: 'monthly',
      loan_type: 'business',
      interest_rate: '',
      remaining_balance: '',
      notes: ''
    });
  };

  const getLoanTypeIcon = (type) => {
    const loan = LOAN_TYPES.find(l => l.value === type);
    return loan ? loan.icon : Building2;
  };

  const filteredPayments = activeTab === 'all' 
    ? payments 
    : payments.filter(p => p.payment_type === activeTab);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 text-indigo-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-4"
          >
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-100 dark:bg-indigo-900/40 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-indigo-700 dark:text-indigo-400" />
              </div>
              <div>
                <p className="text-xs text-slate-500 dark:text-slate-400">Monthly Total</p>
                <p className="text-lg font-bold text-slate-900 dark:text-white">
                  ${summary.total_monthly.toLocaleString()}
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-4"
          >
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/40 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-purple-700 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-xs text-slate-500 dark:text-slate-400">Loans ({summary.loans_count})</p>
                <p className="text-lg font-bold text-slate-900 dark:text-white">
                  ${summary.total_loans_monthly.toLocaleString()}/mo
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-4"
          >
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center">
                <Home className="w-5 h-5 text-emerald-700 dark:text-emerald-400" />
              </div>
              <div>
                <p className="text-xs text-slate-500 dark:text-slate-400">Rent ({summary.rent_count})</p>
                <p className="text-lg font-bold text-slate-900 dark:text-white">
                  ${summary.total_rent_monthly.toLocaleString()}/mo
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-4"
          >
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-lg bg-rose-100 dark:bg-rose-900/40 flex items-center justify-center">
                <TrendingDown className="w-5 h-5 text-rose-700 dark:text-rose-400" />
              </div>
              <div>
                <p className="text-xs text-slate-500 dark:text-slate-400">Loan Balance</p>
                <p className="text-lg font-bold text-slate-900 dark:text-white">
                  ${summary.total_loan_balance.toLocaleString()}
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Upcoming Payments Alert */}
      {upcoming && upcoming.count > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-xl p-4"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              <div>
                <p className="font-medium text-amber-900 dark:text-amber-200">
                  {upcoming.count} payment(s) due in next 30 days
                </p>
                <p className="text-sm text-amber-700 dark:text-amber-300">
                  Total: ${upcoming.total_due.toLocaleString()}
                </p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-amber-600" />
          </div>
        </motion.div>
      )}

      {/* Tab Navigation + Add Button */}
      <div className="flex items-center justify-between">
        <div className="flex space-x-1 bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
          {[
            { id: 'all', label: 'All' },
            { id: 'loan', label: 'Loans' },
            { id: 'rent', label: 'Rent' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
                activeTab === tab.id
                  ? 'bg-white dark:bg-slate-700 text-indigo-700 dark:text-indigo-300 shadow-sm'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <button
          onClick={() => { resetForm(); setEditingPayment(null); setShowForm(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          data-testid="add-payment-btn"
        >
          <Plus className="w-4 h-4" />
          Add Payment
        </button>
      </div>

      {/* Payments List */}
      <div className="space-y-3">
        {filteredPayments.length > 0 ? (
          filteredPayments.map((payment) => {
            const Icon = payment.payment_type === 'loan' ? getLoanTypeIcon(payment.loan_type) : Home;
            return (
              <motion.div
                key={payment.payment_id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-4"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      payment.payment_type === 'loan' 
                        ? 'bg-purple-100 dark:bg-purple-900/40' 
                        : 'bg-emerald-100 dark:bg-emerald-900/40'
                    }`}>
                      <Icon className={`w-6 h-6 ${
                        payment.payment_type === 'loan' 
                          ? 'text-purple-700 dark:text-purple-400' 
                          : 'text-emerald-700 dark:text-emerald-400'
                      }`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-slate-900 dark:text-white">{payment.name}</h3>
                        <span className={`px-2 py-0.5 text-xs rounded-full ${
                          payment.payment_type === 'loan'
                            ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                            : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                        }`}>
                          {payment.payment_type === 'loan' ? payment.loan_type : 'rent'}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-sm text-slate-500 dark:text-slate-400">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          Day {payment.due_day} • {payment.frequency}
                        </span>
                        {payment.interest_rate && (
                          <span className="flex items-center gap-1">
                            <Percent className="w-3.5 h-3.5" />
                            {payment.interest_rate}% APR
                          </span>
                        )}
                      </div>
                      {payment.remaining_balance && (
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                          Remaining: ${payment.remaining_balance.toLocaleString()}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold text-slate-900 dark:text-white">
                      ${payment.amount.toLocaleString()}
                    </p>
                    <p className="text-xs text-slate-500">/{payment.frequency === 'monthly' ? 'mo' : payment.frequency}</p>
                    <div className="flex gap-1 mt-2">
                      <button
                        onClick={() => handleEdit(payment)}
                        className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                      >
                        <Edit2 className="w-4 h-4 text-slate-500" />
                      </button>
                      <button
                        onClick={() => handleDelete(payment.payment_id)}
                        className="p-1.5 hover:bg-rose-100 dark:hover:bg-rose-900/30 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4 text-rose-500" />
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })
        ) : (
          <div className="text-center py-12 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
            <CreditCard className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-600 dark:text-slate-400">No recurring payments yet</p>
            <button
              onClick={() => setShowForm(true)}
              className="mt-3 text-indigo-600 hover:text-indigo-700 text-sm font-medium"
            >
              Add your first payment
            </button>
          </div>
        )}
      </div>

      {/* Add/Edit Form Modal */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowForm(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                    {editingPayment ? 'Edit Payment' : 'Add Recurring Payment'}
                  </h2>
                  <button
                    onClick={() => setShowForm(false)}
                    className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg"
                  >
                    <X className="w-5 h-5 text-slate-500" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Payment Type */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Payment Type
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, payment_type: 'loan' })}
                        className={`p-3 rounded-lg border-2 transition-all flex items-center gap-2 ${
                          formData.payment_type === 'loan'
                            ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/30'
                            : 'border-slate-200 dark:border-slate-700'
                        }`}
                      >
                        <Building2 className="w-5 h-5" />
                        <span className="font-medium">Loan</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, payment_type: 'rent' })}
                        className={`p-3 rounded-lg border-2 transition-all flex items-center gap-2 ${
                          formData.payment_type === 'rent'
                            ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/30'
                            : 'border-slate-200 dark:border-slate-700'
                        }`}
                      >
                        <Home className="w-5 h-5" />
                        <span className="font-medium">Rent</span>
                      </button>
                    </div>
                  </div>

                  {/* Loan Type (if loan) */}
                  {formData.payment_type === 'loan' && (
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Loan Type
                      </label>
                      <div className="grid grid-cols-3 gap-2">
                        {LOAN_TYPES.map(loan => (
                          <button
                            key={loan.value}
                            type="button"
                            onClick={() => setFormData({ ...formData, loan_type: loan.value })}
                            className={`p-2 rounded-lg border-2 transition-all flex flex-col items-center gap-1 ${
                              formData.loan_type === loan.value
                                ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/30'
                                : 'border-slate-200 dark:border-slate-700'
                            }`}
                          >
                            <loan.icon className="w-5 h-5" />
                            <span className="text-xs font-medium">{loan.label.split(' ')[0]}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Name */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      Payment Name
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder={formData.payment_type === 'loan' ? 'e.g., Business Expansion Loan' : 'e.g., Office Rent'}
                      className="w-full px-4 py-2.5 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-slate-800 dark:text-white"
                      required
                    />
                  </div>

                  {/* Amount */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      Payment Amount
                    </label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        type="number"
                        value={formData.amount}
                        onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                        placeholder="0.00"
                        className="w-full pl-9 pr-4 py-2.5 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-slate-800 dark:text-white"
                        required
                      />
                    </div>
                  </div>

                  {/* Due Day & Frequency */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                        Due Day
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="31"
                        value={formData.due_day}
                        onChange={(e) => setFormData({ ...formData, due_day: e.target.value })}
                        className="w-full px-4 py-2.5 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-slate-800 dark:text-white"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                        Frequency
                      </label>
                      <select
                        value={formData.frequency}
                        onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
                        className="w-full px-4 py-2.5 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-slate-800 dark:text-white"
                      >
                        {FREQUENCIES.map(freq => (
                          <option key={freq.value} value={freq.value}>{freq.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Loan-specific fields */}
                  {formData.payment_type === 'loan' && (
                    <>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                            Interest Rate (% APR)
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            value={formData.interest_rate}
                            onChange={(e) => setFormData({ ...formData, interest_rate: e.target.value })}
                            placeholder="e.g., 5.5"
                            className="w-full px-4 py-2.5 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-slate-800 dark:text-white"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                            Remaining Balance
                          </label>
                          <div className="relative">
                            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <input
                              type="number"
                              value={formData.remaining_balance}
                              onChange={(e) => setFormData({ ...formData, remaining_balance: e.target.value })}
                              placeholder="0.00"
                              className="w-full pl-9 pr-4 py-2.5 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-slate-800 dark:text-white"
                            />
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Notes */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      Notes (optional)
                    </label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Any additional notes..."
                      rows={2}
                      className="w-full px-4 py-2.5 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-slate-800 dark:text-white resize-none"
                    />
                  </div>

                  {/* Submit */}
                  <div className="flex gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => setShowForm(false)}
                      className="flex-1 px-4 py-2.5 border border-slate-200 dark:border-slate-700 rounded-lg font-medium hover:bg-slate-50 dark:hover:bg-slate-800"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="flex-1 px-4 py-2.5 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 flex items-center justify-center gap-2"
                    >
                      <Check className="w-4 h-4" />
                      {editingPayment ? 'Update' : 'Add Payment'}
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default RecurringPayments;
